import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShareReferenceLocationState } from '$types/LocationState';
import {
  createPositionWithSharedRoster,
  duplicatePositionAfterLogin,
} from '$api/auth.service';
import { userLanguage } from '$translations/index';
import { warningHandler } from '$store/utils/warning-handler';
import { ErrorMessageEs, ErrorMessageEn } from '$constants/alerts';
import EmployerSPKLayout, {
  ActiveEmployerLayoutPageType,
} from '$components/EmployerDashboard/EmployerSPKLayout';
import SPKToast from '$components/SPKToast/SPKToast';
import { useTranslation } from 'react-i18next';
import PositionPage from '../Positions/PositionPage';

type EmployerAddPositionState = {
  state: { showSmartInterviewActivatedToast: boolean };
  pathname: string;
};

type PositionPageLayoutStates =
  ShareReferenceLocationState | EmployerAddPositionState;

const PositionPageLayout = () => {
  const location = useLocation() as PositionPageLayoutStates;
  const navigate = useNavigate();
  const [t] = useTranslation('global');

  useEffect(() => {
    if (
      location?.state &&
      'showSmartInterviewActivatedToast' in location.state
    ) {
      if (location.state.showSmartInterviewActivatedToast)
        SPKToast(t('EmployerAddPosition.SmartInterviewsActivated'));
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate, t]);

  useEffect(() => {
    (async () => {
      if (location?.state && 'shareReferenceId' in location.state) {
        await duplicatePositionAfterLogin(
          location?.state?.shareReferenceId as string
        ).catch((response) => {
          const errorType =
            response?.response?.data?.statusCode === 400
              ? 'AlreadyDuplicatedPosition'
              : 'DuplicateGeneralErrorMessage';
          const errorMessage =
            userLanguage === 'es'
              ? ErrorMessageEs[errorType]
              : ErrorMessageEn[errorType];
          warningHandler(
            '[DuplicatePositionAfterLogin]',
            errorMessage,
            response,
            true
          );
        });
      }

      if (location?.state && 'shareCandidatesIds' in location.state) {
        await createPositionWithSharedRoster(
          location?.state?.shareCandidatesIds as string
        ).catch((response) => {
          const errorMessage =
            userLanguage === 'es'
              ? ErrorMessageEs.CreatePositionWithSharedRosterError
              : ErrorMessageEn.CreatePositionWithSharedRosterError;
          warningHandler(
            '[CreatePositionWithSharedRoster]',
            errorMessage,
            response,
            true
          );
        });
      }

      // Avoids user calling again multiple times to the endpoint if he hits refresh
      if (
        location.state &&
        ('shareCandidatesIds' in location.state ||
          'shareReferenceId' in location.state)
      ) {
        navigate(location.pathname, { replace: true });
      }
    })();
  }, [location, navigate]);

  return (
    <EmployerSPKLayout
      externalIsModalPositionOpen={false}
      activePage={ActiveEmployerLayoutPageType.positions}
    >
      <PositionPage />
    </EmployerSPKLayout>
  );
};

export default PositionPageLayout;
